import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '@/shared/lib'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'icon'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  children: ReactNode
}

const base =
  'inline-flex items-center justify-center gap-2 font-mono font-medium uppercase tracking-wide rounded-control transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent disabled:opacity-40 disabled:pointer-events-none'

const byVariant: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-text-inverse hover:bg-accent-hover shadow-glow-accent',
  secondary:
    'border border-border-strong text-text-primary hover:border-text-primary hover:bg-bg-elevated',
  ghost: 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated',
  danger: 'border border-border-accent text-accent hover:bg-accent hover:text-text-inverse',
}

// Вертикальные паддинги асимметричны намеренно: у mono-каps текста пустует
// зона десцендеров, оптический центр выше геометрического — сдвигаем текст
// на 1px вниз, сохраняя высоту кнопки.
const bySize: Record<ButtonSize, string> = {
  sm: 'text-xs px-3 pt-[9px] pb-[7px]',
  md: 'text-sm px-5 pt-[13px] pb-[11px]',
  // Квадрат 32×32 под одиночную иконку 16px — высота совпадает с sm.
  // Icon-only кнопке обязателен aria-label.
  icon: 'p-2',
}

export function Button({
  variant = 'primary',
  size = 'md',
  type,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type ?? 'button'}
      className={cn(base, byVariant[variant], bySize[size], className)}
      {...rest}
    >
      {children}
    </button>
  )
}
